import React, { useEffect, useState } from 'react';
import ChatContainer from './components/Chat/ChatContainer';
import LoginPage from './components/Auth/LoginPage';
import CallbackPage from './components/Auth/CallbackPage';
import { useAuth } from './hooks/useAuth';
import { setOnUnauthorized } from './api/client';
import { preserveRequestedContext } from './utils/requestedContext';
import { isDevAnalyticsHost, useAnalyticsInitialization, useAnalyticsPageViews } from './services/analytics';
import './index.css';
import { useProductConsent } from './hooks/useProductConsent';
import PrivacyConsent from './components/Privacy/PrivacyConsent';

function App() {
  const { token, user, isAuthenticated, isLoading, error, authMode, loginWithKeycloak, loginDev, handleCallback, logout } = useAuth();
  const analyticsUserId = user?.email;
  const consent = useProductConsent(isAuthenticated ? token : null, analyticsUserId);
  const [privacySettingsOpen, setPrivacySettingsOpen] = useState(false);

  useAnalyticsInitialization();
  useAnalyticsPageViews();

  useEffect(() => {
    setOnUnauthorized(logout);
  }, [logout]);

  // Auto-redirect to Keycloak when auth mode is known and user isn't authenticated
  // Skip if user just logged out (prevent immediate re-login loop)
  const justLoggedOut = new URLSearchParams(window.location.search).has('logged_out');

  useEffect(() => {
    if (!isAuthenticated && authMode === 'keycloak' && !isLoading && !error && !justLoggedOut) {
      preserveRequestedContext();
      loginWithKeycloak();
    }
  }, [isAuthenticated, authMode, isLoading, error, loginWithKeycloak, justLoggedOut]);

  // Handle OAuth2 callback route
  if (window.location.pathname === '/callback') {
    return <CallbackPage onCallback={handleCallback} />;
  }

  if (!isAuthenticated) {
    // User just logged out — show signed-out confirmation
    if (justLoggedOut) {
      const handleSignInAgain = () => {
        // Remove the logged_out param and reload to trigger auto-login
        window.location.href = window.location.origin;
      };
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: '#121212',
          color: '#9ca3af',
          flexDirection: 'column',
          gap: '1rem',
        }}>
          <span style={{ fontSize: '1rem', color: '#e5e7eb' }}>You've been signed out</span>
          <button
            onClick={handleSignInAgain}
            style={{
              padding: '0.625rem 1.5rem',
              background: '#8b5cf6',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Sign in again
          </button>
        </div>
      );
    }
    // In keycloak mode, show a minimal loading state while redirecting
    if (authMode === 'keycloak' || authMode === 'unknown') {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: '#121212',
          color: '#9ca3af',
          flexDirection: 'column',
          gap: '1rem',
        }}>
          <span style={{
            display: 'inline-block',
            width: '24px',
            height: '24px',
            border: '2px solid rgba(255,255,255,0.2)',
            borderTopColor: '#8b5cf6',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite',
          }} />
          {error ? (
            <span style={{ color: '#ef4444', fontSize: '0.875rem' }}>{error}</span>
          ) : (
            <span style={{ fontSize: '0.875rem' }}>Signing in…</span>
          )}
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      );
    }
    // Dev mode: show the full login page with form
    return (
      <LoginPage onLogin={loginWithKeycloak} onDevLogin={loginDev} authMode={authMode} isLoading={isLoading} error={error} />
    );
  }

  return (
    <>
      <ChatContainer
        onLogout={logout}
        userEmail={user?.email}
        userName={user?.name}
        userId={analyticsUserId}
        authToken={token}
        onPrivacySettings={isDevAnalyticsHost() ? () => setPrivacySettingsOpen(true) : undefined}
      />
      <PrivacyConsent key={user?.sub || analyticsUserId} {...consent}
        settingsOpen={privacySettingsOpen} onCloseSettings={() => setPrivacySettingsOpen(false)}
        onDecide={consent.decide} onRetry={consent.retry} />
    </>
  );
}

export default App;
